// src/pages/network/PostDetail.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  ArrowLeft, Clock, ThumbsUp, MessageSquare, Share2,
  AlertCircle, Check, FileText, Download,
  Newspaper, CalendarDays, MapPin, Ticket
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CommentSection from '../../components/network/interactions/CommentSection';
import ReactionList from '../../components/network/interactions/ReactionList';
import RelatedPosts from '../../components/network/feed/RelatedPosts';
import PostActionsMenu from '../../components/network/feed/PostActionsMenu';
import PostEditModal from '../../components/network/feed/PostEditModal';
import PostReportModal from '../../components/network/feed/PostReportModal';
import DeletePostModal from '../../components/network/feed/DeletePostModal';
import { db } from '../../firebase/config';
import {
  collection, doc, setDoc, getDoc, updateDoc, deleteDoc, increment,
  serverTimestamp, getDocs, query, where
} from 'firebase/firestore';

const PostDetail = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const commentSectionRef = useRef(null);

  const [post, setPost] = useState(null);
  const [author, setAuthor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [saved, setSaved] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');
  const [reporting, setReporting] = useState(false);
  const [reportError, setReportError] = useState('');
  const [deleting, setDeleting] = useState(false);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    });
  };

  const incrementShareCount = async () => {
    try {
      await updateDoc(doc(db, 'posts', postId), {
        shareCount: increment(1)
      });
      setPost((prev) => prev ? {
        ...prev,
        shareCount: (prev.shareCount || 0) + 1
      } : prev);
    } catch (err) {
      console.error('Error incrementing share count:', err);
    }
  };

  // Fetch post data
  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError(null);

        const postRef = doc(db, 'posts', postId);
        const postSnap = await getDoc(postRef);

        if (!postSnap.exists()) {
          setError('Post not found');
          setLoading(false);
          return;
        }

        const postData = {
          id: postSnap.id,
          ...postSnap.data(),
          createdAt: postSnap.data().createdAt?.toDate() || new Date()
        };

        setPost(postData);
        setLikeCount(postData.likeCount || 0);

        // Fetch author info
        if (postData.authorId) {
          const authorRef = doc(db, 'businesses', postData.authorId);
          const authorSnap = await getDoc(authorRef);

          if (authorSnap.exists()) {
            setAuthor({
              id: authorSnap.id,
              ...authorSnap.data()
            });
          }
        }

        // Check if user has liked the post
        if (currentUser) {
          const likeRef = doc(db, 'postLikes', `${currentUser.uid}_${postId}`);
          const likeSnap = await getDoc(likeRef);
          setLiked(likeSnap.exists());

          // Check if user has saved the post
          const savedPostRef = doc(db, 'savedPosts', `${currentUser.uid}_${postId}`);
          const savedPostSnap = await getDoc(savedPostRef);
          setSaved(savedPostSnap.exists());
        }

        // Public shared-post views should still render even when the visitor
        // cannot update counters anonymously.
        try {
          await updateDoc(postRef, {
            viewCount: increment(1)
          });
        } catch (viewCountError) {
          console.error('Error incrementing view count:', viewCountError);
        }

      } catch (err) {
        console.error('Error fetching post:', err);
        setError('Failed to load post');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [postId, currentUser]);

  // Handle like/unlike
  const handleToggleLike = async () => {
    if (!currentUser) {
      navigate('/login', { state: { redirectTo: `/network/post/${postId}` } });
      return;
    }

    try {
      const likeRef = doc(db, 'postLikes', `${currentUser.uid}_${postId}`);
      const postRef = doc(db, 'posts', postId);

      if (liked) {
        // Unlike
        await deleteDoc(likeRef);
        await updateDoc(postRef, {
          likeCount: increment(-1)
        });
        setLiked(false);
        setLikeCount(prev => prev - 1);
      } else {
        // Like
        await setDoc(likeRef, {
          userId: currentUser.uid,
          postId,
          createdAt: serverTimestamp()
        });
        await updateDoc(postRef, {
          likeCount: increment(1)
        });
        setLiked(true);
        setLikeCount(prev => prev + 1);
      }
    } catch (err) {
      console.error('Error toggling like:', err);
    }
  };

  // Handle save/unsave
  const handleToggleSave = async () => {
    if (!currentUser) {
      navigate('/login', { state: { redirectTo: `/network/post/${postId}` } });
      return;
    }

    try {
      const savedPostRef = doc(db, 'savedPosts', `${currentUser.uid}_${postId}`);

      if (saved) {
        // Unsave
        await deleteDoc(savedPostRef);
        setSaved(false);
      } else {
        // Save
        await setDoc(savedPostRef, {
          userId: currentUser.uid,
          postId,
          createdAt: serverTimestamp()
        });
        setSaved(true);
      }
    } catch (err) {
      console.error('Error toggling save:', err);
    }
  };

  // Handle Comment button - scroll to comment section and focus input
  const handleCommentClick = () => {
    if (commentSectionRef.current) {
      commentSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Focus the comment input after scroll
      setTimeout(() => {
        const input = commentSectionRef.current?.querySelector('input[type="text"], textarea');
        if (input) input.focus();
      }, 400);
    }
  };

  // Handle Share button
  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/share/post/${postId}`;
    const shareData = {
      title: post?.title || (author?.name ? `Post by ${author.name}` : 'Post on BizCentral'),
      text: post?.subtitle || post?.content?.slice(0, 100) || 'Check out this post on BizCentral',
      url: shareUrl
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        await incrementShareCount();
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyToClipboard(shareUrl);
          await incrementShareCount();
        }
      }
    } else {
      copyToClipboard(shareUrl);
      await incrementShareCount();
    }
  };

  const isOwner = !!currentUser && currentUser.uid === post?.authorId;

  const handleSaveEdit = async (formData) => {
    if (!isOwner || !post) {
      return;
    }

    const trimmedContent = formData.content.trim();
    const trimmedTitle = formData.title.trim();

    if (!trimmedContent) {
      setEditError('Please enter some content for your post.');
      return;
    }

    if ((post.postType === 'article' || post.postType === 'event') && !trimmedTitle) {
      setEditError('Please enter a title before saving.');
      return;
    }

    if (post.postType === 'event' && !formData.eventDate) {
      setEditError('Please select an event date before saving.');
      return;
    }

    try {
      setSavingEdit(true);
      setEditError('');

      const updateData = {
        content: trimmedContent,
        updatedAt: serverTimestamp()
      };

      if (post.postType === 'article' || post.postType === 'event') {
        updateData.title = trimmedTitle;
      }

      if (post.postType === 'article') {
        updateData.subtitle = formData.subtitle.trim() || null;
      }

      if (post.postType === 'event') {
        updateData.eventVenue = formData.eventVenue.trim() || null;
        updateData.eventDate = formData.eventDate || null;
        updateData.eventBookingLink = formData.eventBookingLink.trim() || null;
      }

      await updateDoc(doc(db, 'posts', postId), updateData);
      setPost((prev) => ({ ...prev, ...updateData, updatedAt: new Date() }));
      setShowEditModal(false);
    } catch (error) {
      console.error('Error updating post:', error);
      setEditError('Failed to save changes. Please try again.');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleOpenReport = () => {
    if (!currentUser) {
      navigate('/login', { state: { redirectTo: `/network/post/${postId}` } });
      return;
    }

    setReportError('');
    setShowReportModal(true);
  };

  const handleSubmitReport = async ({ reason, details }) => {
    if (!currentUser || !post) {
      return;
    }

    try {
      setReporting(true);
      setReportError('');

      await setDoc(doc(db, 'postReports', `${currentUser.uid}_${post.id}`), {
        postId: post.id,
        reporterId: currentUser.uid,
        authorId: post.authorId || null,
        reason,
        details: details || null,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      setShowReportModal(false);
    } catch (error) {
      console.error('Error reporting post:', error);
      setReportError('Failed to submit the report. Please try again.');
    } finally {
      setReporting(false);
    }
  };

  const handleDeletePost = async () => {
    if (!isOwner || !post) {
      return;
    }

    try {
      setDeleting(true);

      const deleteMatches = async (collectionName) => {
        const snapshot = await getDocs(query(collection(db, collectionName), where('postId', '==', post.id)));
        await Promise.all(snapshot.docs.map((item) => deleteDoc(item.ref)));
      };

      await Promise.all([
        deleteMatches('comments'),
        deleteMatches('postLikes'),
        deleteMatches('savedPosts'),
        deleteMatches('postReports')
      ]);

      await deleteDoc(doc(db, 'posts', post.id));
      navigate('/network');
    } catch (error) {
      console.error('Error deleting post:', error);
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-[#190192] rounded-full border-t-transparent"></div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm p-8 text-center">
        <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
        <h2 className="text-xl font-medium text-gray-900 mb-2">Post Not Found</h2>
        <p className="text-gray-500 mb-6">The post you're looking for doesn't exist or has been removed.</p>
        <Link
          to="/network"
          className="px-4 py-2 bg-[#190192] text-white rounded-lg hover:bg-[#2C0DB5] inline-flex items-center"
        >
          <ArrowLeft size={16} className="mr-2" /> Back to Network
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-4xl px-4">
      <div className="mb-6">
        <button
          onClick={() => navigate('/network')}
          className="text-[#190192] hover:text-[#2C0DB5] inline-flex items-center"
        >
          <ArrowLeft size={16} className="mr-1" />
          Back to Network
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        {/* Post Header */}
        <div className="flex items-center p-6 border-b border-gray-100">
          <img
            src={author?.profileImage || '/default-business-avatar.png'}
            alt={author?.name || 'Business'}
            className="w-12 h-12 rounded-full mr-4 object-cover"
          />
          <div className="flex-1">
            <Link
              to={`/network/profile/${author?.id}`}
              className="font-medium text-gray-900 hover:text-[#190192]"
            >
              {author?.name || 'Business Name'}
            </Link>
            <div className="flex items-center text-gray-500 text-sm">
              <Clock size={14} className="mr-1" />
              <span>{formatDistanceToNow(post.createdAt)} ago</span>
            </div>
          </div>
          <PostActionsMenu
            isOwner={isOwner}
            isSaved={saved}
            onToggleSave={handleToggleSave}
            onEdit={() => {
              setEditError('');
              setShowEditModal(true);
            }}
            onReport={handleOpenReport}
            onDelete={() => setShowDeleteModal(true)}
          />
        </div>

        {/* Post Content */}
        <div className="px-6 pt-6">
          {/* Article header */}
          {post.postType === 'article' && post.title && (
            <div className="mb-3">
              <div className="flex items-center gap-1.5 text-xs text-[#190192] font-medium mb-2">
                <Newspaper size={12} />
                <span>ARTICLE</span>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 leading-tight">{post.title}</h1>
              {post.subtitle && (
                <p className="text-base text-gray-500 mt-1">{post.subtitle}</p>
              )}
            </div>
          )}

          {/* Event header */}
          {post.postType === 'event' && post.title && (
            <div className="mb-3">
              <div className="flex items-center gap-1.5 text-xs text-[#190192] font-medium mb-2">
                <CalendarDays size={12} />
                <span>EVENT</span>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 leading-tight">{post.title}</h1>
              <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-600">
                {post.eventDate && (
                  <div className="flex items-center gap-2 bg-purple-50 px-3 py-1.5 rounded-full">
                    <CalendarDays size={14} className="text-[#190192]" />
                    <span>{new Date(post.eventDate).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                )}
                {post.eventVenue && (
                  <div className="flex items-center gap-2 bg-purple-50 px-3 py-1.5 rounded-full">
                    <MapPin size={14} className="text-[#190192]" />
                    <span>{post.eventVenue}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="prose prose-lg max-w-none text-gray-800 prose-headings:text-gray-900 prose-a:text-[#190192] prose-a:no-underline hover:prose-a:underline prose-img:rounded-lg prose-pre:bg-gray-50 prose-code:text-[#190192] prose-code:bg-purple-50 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 prose-headings:mb-2 prose-headings:mt-4 prose-blockquote:my-2 prose-pre:my-2 prose-hr:my-3">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
          </div>

          {/* Event booking link */}
          {post.postType === 'event' && post.eventBookingLink && (
            <div className="mt-4">
              <a
                href={post.eventBookingLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#190192] text-white rounded-lg font-medium hover:bg-[#2C0DB5] transition-colors"
              >
                <Ticket size={18} />
                Book / RSVP
              </a>
            </div>
          )}
        </div>

        {post.imageUrl && (
          <div className="mt-3">
            <img
              src={post.imageUrl}
              alt="Post attachment"
              className="w-full object-cover"
            />
          </div>
        )}

        {/* YouTube Video Embed */}
        {post.videoUrl && (
          <div className="mt-3 aspect-video">
            <iframe
              src={`${post.videoUrl}?autoplay=1&mute=1&loop=1`}
              title="Video"
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        )}

        {/* Document Attachment */}
        {post.documentUrl && (
          <div className="mt-3 mx-6">
            <a
              href={post.documentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
            >
              <FileText size={28} className="text-[#190192] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {post.documentName || 'Document'}
                </p>
                <p className="text-xs text-gray-500">Tap to view document</p>
              </div>
              <Download size={20} className="text-gray-400 flex-shrink-0" />
            </a>
          </div>
        )}

        <div className="px-6 pb-6">
          {/* Tags */}
          {post.tags?.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {post.tags.map(tag => (
                <Link
                  key={tag}
                  to={`/network/tag/${tag}`}
                  className="px-3 py-1 bg-purple-50 text-[#190192] text-xs rounded-full hover:bg-purple-100"
                >
                  #{tag}
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Post Actions */}
        <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <button
              className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-md ${
                liked ? 'text-[#190192] bg-purple-50' : 'text-gray-500 hover:bg-gray-100'
              }`}
              onClick={handleToggleLike}
            >
              {likeCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-gray-700 text-white text-[10px] font-medium rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none">
                  {likeCount > 99 ? '99+' : likeCount}
                </span>
              )}
              <ThumbsUp size={18} className={liked ? 'fill-current' : ''} />
              <span>{liked ? 'Liked' : 'Like'}</span>
            </button>

            <button
              className="relative flex items-center gap-1.5 px-3 py-1.5 rounded-md text-gray-500 hover:bg-gray-100"
              onClick={handleCommentClick}
            >
              {(post.commentCount || 0) > 0 && (
                <span className="absolute -top-2 -right-2 bg-gray-700 text-white text-[10px] font-medium rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none">
                  {post.commentCount > 99 ? '99+' : post.commentCount}
                </span>
              )}
              <MessageSquare size={18} />
              <span>Comment</span>
            </button>

            <button
              className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-md ${
                shareCopied ? 'text-green-600 bg-green-50' : 'text-gray-500 hover:bg-gray-100'
              }`}
              onClick={handleShare}
            >
              {(post.shareCount || 0) > 0 && (
                <span className="absolute -top-2 -right-2 bg-gray-700 text-white text-[10px] font-medium rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none">
                  {post.shareCount > 99 ? '99+' : post.shareCount}
                </span>
              )}
              {shareCopied ? <Check size={18} /> : <Share2 size={18} />}
              <span>{shareCopied ? 'Copied!' : 'Share'}</span>
            </button>
          </div>

          <div className="text-xs text-gray-500">
            {(post.viewCount || 0) > 0 ? `${post.viewCount > 99 ? '99+' : post.viewCount} views` : ' '}
          </div>
        </div>

        {/* Comments Section */}
        <div className="border-t border-gray-100" ref={commentSectionRef}>
          <CommentSection
            postId={post.id}
            isAuthenticated={!!currentUser}
          />
        </div>
      </div>

      {/* Related Posts */}
      <div className="mb-8">
        <h3 className="text-lg font-medium text-gray-900 mb-4">You might also be interested in</h3>
        <RelatedPosts
          currentPostId={post.id}
          tags={post.tags || []}
          authorId={post.authorId}
        />
      </div>

      {/* Reaction List Modal */}
      {showReactions && (
        <ReactionList
          postId={post.id}
          onClose={() => setShowReactions(false)}
        />
      )}

      <PostEditModal
        post={post}
        isOpen={showEditModal}
        saving={savingEdit}
        error={editError}
        onClose={() => setShowEditModal(false)}
        onSave={handleSaveEdit}
      />

      <PostReportModal
        isOpen={showReportModal}
        submitting={reporting}
        error={reportError}
        onClose={() => setShowReportModal(false)}
        onSubmit={handleSubmitReport}
      />

      <DeletePostModal
        isOpen={showDeleteModal}
        deleting={deleting}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeletePost}
      />
    </div>
  );
};

export default PostDetail;
